import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveStoreId } from "../campaigns/useActiveStoreId";

interface CustomerFromCoupons {
  id: string;
  name: string;
  email: string;
  phone: string;
  platform: string;
  totalSpent: string;
  purchases: number;
  status: string;
  lastPurchaseDate: string;
  acquisitionSource: string;
  bonusUsed: string;
  cashbackBalance: string; // Novo campo para saldo de cashback
}

export const useCustomersFromCoupons = () => {
  const { data: storeId, isLoading: isLoadingStore } = useActiveStoreId();

  return useQuery({
    queryKey: ['customers-from-coupons', storeId],
    queryFn: async () => {
      if (!storeId) {
        console.warn('❌ No active store ID found');
        return [];
      }

      console.log('🔍 Fetching customers from coupons for store:', storeId);

      const { data, error } = await supabase
        .from('campaign_generated_coupons')
        .select(`
          customer_email,
          customer_name,
          customer_phone,
          total_spent_amount,
          coupon_value,
          created_at,
          status
        `)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching coupons:', error);
        throw error;
      }

      console.log('✅ Coupons fetched:', data?.length || 0);

      // Agrupar por cliente e calcular métricas
      const customersMap = new Map<string, {
        email: string;
        name: string;
        phone: string;
        totalSpent: number;
        purchases: number;
        bonusUsed: number;
        cashbackBalance: number; // Saldo de cashback válido
        lastPurchaseDate: Date;
        firstPurchaseDate: Date;
      }>();

      data?.forEach(coupon => {
        const email = coupon.customer_email;
        if (!email) return;

        const existing = customersMap.get(email);
        const purchaseDate = new Date(coupon.created_at);
        const isValidCoupon = ['active', 'pending'].includes(coupon.status); // Cupons válidos
        
        if (existing) {
          existing.totalSpent += coupon.total_spent_amount || 0;
          existing.purchases += 1;
          existing.bonusUsed += coupon.coupon_value || 0;
          
          // Somar apenas cupons válidos para o saldo de cashback
          if (isValidCoupon) {
            existing.cashbackBalance += coupon.coupon_value || 0;
          }
          
          if (purchaseDate > existing.lastPurchaseDate) {
            existing.lastPurchaseDate = purchaseDate;
          }
          if (purchaseDate < existing.firstPurchaseDate) {
            existing.firstPurchaseDate = purchaseDate;
          }
        } else {
          customersMap.set(email, {
            email,
            name: coupon.customer_name || 'Nome não informado',
            phone: coupon.customer_phone || 'Telefone não informado',
            totalSpent: coupon.total_spent_amount || 0,
            purchases: 1,
            bonusUsed: coupon.coupon_value || 0,
            cashbackBalance: isValidCoupon ? (coupon.coupon_value || 0) : 0, // Inicializar saldo
            lastPurchaseDate: purchaseDate,
            firstPurchaseDate: purchaseDate
          });
        }
      });

      // Converter para formato da UI
      const mappedCustomers: CustomerFromCoupons[] = Array.from(customersMap.values()).map((customer, index) => ({
        id: `customer-${index}`,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        platform: 'nuvemshop',
        totalSpent: `R$ ${customer.totalSpent.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        purchases: customer.purchases,
        status: 'active',
        lastPurchaseDate: customer.lastPurchaseDate.toLocaleDateString('pt-BR'),
        acquisitionSource: 'Cupons de Cashback',
        bonusUsed: `R$ ${customer.bonusUsed.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        cashbackBalance: `R$ ${customer.cashbackBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
      }));

      // Ordenar por total gasto (maior primeiro)
      mappedCustomers.sort((a, b) => {
        const aValue = parseFloat(a.totalSpent.replace(/[^\d,]/g, '').replace(',', '.'));
        const bValue = parseFloat(b.totalSpent.replace(/[^\d,]/g, '').replace(',', '.'));
        return bValue - aValue;
      });

      console.log('✅ Customers processed:', mappedCustomers.length);
      return mappedCustomers;
    },
    enabled: !!storeId && !isLoadingStore,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3
  });
};